import Link from 'next/link';
import { Auth } from 'aws-amplify';
import { Public, Button, P, Meta, ErrorBanner, inputClasses, cn} from '../lib/ui';
import { useForm } from 'react-hook-form';
import { useState } from 'react';

type Props = {
  className?: string;
};

type Inputs = {
  email: string;
};

const emailOpts = {
  required: 'A valid email address is required.',
  pattern: {
    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    message: 'A valid email address is required.',
  },
};

const Confirmation = () => (
  <>
    <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
      <svg
        className="h-6 w-6 text-green-600"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        aria-hidden="true">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M5 13l4 4L19 7"
        />
      </svg>
    </div>
    <div className="mt-3 text-center sm:mt-5">
      <h1 className="text-2xl font-semibold tracking-tight text-neutral-900">
        A password reset email was sent!
      </h1>
      <div className="mt-4 mb-2">
        <p className="text-md text-neutral-500">
          Check your inbox for a confirmation link.
        </p>
      </div>
    </div>
  </>
);

const parseErrorFromCognito = (error) => {
  switch (error.code) {
    case 'UserNotFoundException':
      return 'Double check the email address you entered.';
    case 'InvalidParameterException':
      if (error.message.startsWith('Cannot reset password')) {
        return 'Accounts must be confirmed before they can be reset. Check your inbox for the original confirmation link.';
      } else {
        return error.message;
      }
    case 'LimitExceededException':
      return 'Whoa there. Your request attempt limit has been exceeded. Please try again at a later time.'
    case 'UserNotConfirmedException':
      return error.message;
    default:
      return 'An unexpected error occurred. Please try your request again at later time.';
  }
};

export default function Forgot(_props: Props) {
  const {
    register,
    handleSubmit,
    errors,
    formState: { isSubmitting },
  } = useForm<Inputs>();
  const [formError, setFormError] = useState(null);
  const [formSuccess, setFormSuccess] = useState(null);
  const onSubmit = async ({ email }, e) => {
    e.preventDefault();
    setFormError(null);
    try {
      await Auth.forgotPassword(email);
      setFormSuccess(true);
    } catch (error) {
      setFormError(
        parseErrorFromCognito(error) || 'An unknown error occurred on login.'
      );
    }
  };
  const bodyProps = { title: 'Forgot Password' };
  if (formSuccess) {
    delete bodyProps.title;
  }
  return <>
    <Meta title="Forgot Password" />
    <Public.Header />
    <Public.Body {...bodyProps}>
      {formSuccess ? (
        <Confirmation />
      ) : (
        <>
          <form className="space-y-6 mt-8" onSubmit={handleSubmit(onSubmit)}>
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-neutral-700">
                Email
              </label>
              <div className="mt-1">
                <input
                  name="email"
                  id="email"
                  ref={register(emailOpts)}
                  autoComplete="email"
                  className={inputClasses}
                />
              </div>
              {errors?.email && <ErrorBanner title={errors.email.message} />}
            </div>
            {formError && <ErrorBanner title={formError} />}
            <div>
              <Button
                tag="button"
                type="submit"
                id="btn-resetemail"
                disabled={isSubmitting}
                className="w-full flex justify-center">
                {isSubmitting ? 'Sending...' : 'Send Reset Email'}
              </Button>
            </div>
          </form>
          <div className="mt-6 border-t border-subtle pt-4">
            <div>
              <P>
                Already have a Postdrop account?{' '}
                <Link href="/login" className="font-medium text-blue hover:text-blue">
                  
                    Log in to Postdrop
                  
                </Link>
                .
              </P>
              <P>
                First time here?{' '}
                <Link href="/signup" className="font-medium text-blue hover:text-blue">
                  
                    Sign up for a Postdrop account
                  
                </Link>
                .
              </P>
            </div>
          </div>
        </>
      )}
    </Public.Body>
  </>;
}

Forgot.Layout = Public;
